import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation, Outlet } from "react-router-dom";
import {
  ArrowLeftRight,
  BarChart3,
  Building2,
  Coins,
  CreditCard,
  FileCode2,
  Layers,
  LayoutGrid,
  ListTodo,
  Mail,
  Map,
  Package,
  RefreshCw,
  ShieldCheck,
  Store,
} from "lucide-react";
import { useSidebarGroupsStore } from "../stores/sidebarGroupsStore";
import { AppShell, type NavGroup } from "./AppShell";
import { useViewTransitionNavigate } from "../hooks/useViewTransitionNavigate";
import { useLogout } from "../hooks/useLogout";
import { ADMIN_ROUTES } from "../lib/constants/adminRoutes";

// Super-admin nav is static (no permission gating) and small enough that a
// module-level const is the right choice — no useMemo needed.
const ADMIN_NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "admin.nav.group.general",
    items: [
      {
        labelKey: "admin.nav.overview",
        path: ADMIN_ROUTES.root,
        icon: <LayoutGrid size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.tenants",
        path: ADMIN_ROUTES.tenants,
        icon: <Building2 size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.exchangeRates",
        path: ADMIN_ROUTES.exchangeRates,
        icon: <Coins size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.emailSettings",
        path: ADMIN_ROUTES.emailSettings,
        icon: <Mail size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.emailTemplates",
        path: ADMIN_ROUTES.emailTemplates,
        icon: <FileCode2 size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.billingSettings",
        path: ADMIN_ROUTES.billingSettings,
        icon: <CreditCard size={16} aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: "admin.nav.group.storage",
    items: [
      {
        labelKey: "admin.nav.storageProfiles",
        path: ADMIN_ROUTES.storageProfiles,
        icon: <Layers size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.storageMigrations",
        path: ADMIN_ROUTES.storageMigrations,
        icon: <ArrowLeftRight size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.storageOverview",
        path: ADMIN_ROUTES.storageOverview,
        icon: <BarChart3 size={16} aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: "admin.nav.group.catalog",
    items: [
      {
        labelKey: "admin.nav.appCatalogSuggestions",
        path: ADMIN_ROUTES.appCatalogSuggestions,
        icon: <ListTodo size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.appCatalogRoadmap",
        path: ADMIN_ROUTES.appCatalogRoadmap,
        icon: <Map size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.appStoreCatalog",
        path: ADMIN_ROUTES.appstore,
        icon: <Store size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.commercialPolicies",
        path: ADMIN_ROUTES.appstorePolicies,
        icon: <ShieldCheck size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.packaging",
        path: ADMIN_ROUTES.appstorePackaging,
        icon: <Package size={16} aria-hidden="true" />,
      },
    ],
  },
  {
    labelKey: "admin.nav.group.marketplace",
    items: [
      {
        labelKey: "admin.nav.marketplaceStores",
        path: ADMIN_ROUTES.marketplaceStores,
        icon: <Store size={16} aria-hidden="true" />,
      },
      {
        labelKey: "admin.nav.marketplaceCatalog",
        path: ADMIN_ROUTES.marketplaceCatalog,
        icon: <RefreshCw size={16} aria-hidden="true" />,
      },
    ],
  },
];

const AdminLayout: React.FC = () => {
  const navigate = useViewTransitionNavigate();
  const { pathname } = useLocation();
  const expandGroup = useSidebarGroupsStore((s) => s.expandGroup);

  // Active link: longest-prefix match prevents /super-admin/appstore from
  // simultaneously highlighting /super-admin (root) and /super-admin/appstore.
  const bestMatchPath = useMemo(() => {
    let best: string | null = null;
    for (const g of ADMIN_NAV_GROUPS) {
      for (const item of g.items) {
        const p = item.path;
        const matches =
          p === "/super-admin"
            ? pathname === "/super-admin"
            : pathname === p || pathname.startsWith(p + "/");
        if (matches && (best === null || p.length > best.length)) {
          best = p;
        }
      }
    }
    return best;
  }, [pathname]);

  const isActive = useCallback(
    (path: string) => path === bestMatchPath,
    [bestMatchPath],
  );

  // Aktif route'un parent grubunu otomatik aç.
  useEffect(() => {
    const active = ADMIN_NAV_GROUPS.find((g) =>
      g.items.some((i) => isActive(i.path)),
    );
    if (active) expandGroup(active.labelKey);
  }, [pathname, expandGroup, isActive]);

  const { handleLogout, loggingOut, logoutError } = useLogout(navigate);

  return (
    <AppShell
      navGroups={ADMIN_NAV_GROUPS}
      badge={{
        text: "Super",
        className: "text-primary bg-primary/8 border-primary/15",
      }}
      isActive={isActive}
      // Super-admin has no profile or password routes — show logout only.
      profileDropdownItems={[]}
      showNotifications={false}
      showCurrencySwitcher={false}
      handleLogout={handleLogout}
      loggingOut={loggingOut}
      logoutError={logoutError}
      onNavigate={navigate}
      homePath={ADMIN_ROUTES.root}
    >
      <Outlet />
    </AppShell>
  );
};

export default AdminLayout;
